/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { CartLine, DeliveryOption } from '@msdyn365-commerce/retail-proxy';
import { IAny, ICoreContext, IGeneric } from '@msdyn365-commerce/core';
import DescriptionComponent from './checkout-delivery-options.description';
import RadioButtonComponent from './checkout-cartLine-delivery-options-grp';

export interface ICheckoutCartLineDeliveryOptionsRadioButtonProps {
    className: string;
    moduleId: string;
    ariaSetSize: number;
    priceFree: string;
    ariaPosInSet: number;
    selectedItemId: string;
    cartLines: CartLine[];
    deliveryOptions: DeliveryOption[];
    typeName: string;
    context: ICoreContext<IGeneric<IAny>>;
    groupName: string; // Add groupName prop
    ariaLabel?: string;
    value?: string;
    cartLineId?: string;
    telemetryContent?: ITelemetryContent;
    onChange(event: React.ChangeEvent<HTMLInputElement>, cartLines: CartLine[], cartLineId?: string): void;
}

/**
 * Represents a radio button component for checkout cart line delivery options.
 * @param {Object} props - The component props.
 * @param {boolean} props.isChecked - Indicates whether the radio button is checked.
 * @param {string} props.value - The value of the radio button.
 * @param {number} props.ariaSetSize - The total number of radio buttons in the set.
 * @param {number} props.ariaPosInSet - The position of the radio button in the set.
 * @param {string} props.ariaLabel - The ARIA label for the radio button.
 * @param {string} props.telemetryContent - The telemetry content for the radio button.
 * @param {Function} props.onChange - The event handler for when the radio button is changed.
 * @returns {JSX.Element} The rendered radio button component.
 */
const CheckoutCartLineDeliveryOptionsRadio: React.FC<ICheckoutCartLineDeliveryOptionsRadioButtonProps> = ({
    className,
    moduleId,
    ariaSetSize,
    ariaPosInSet,
    ariaLabel,
    telemetryContent,
    deliveryOptions,
    priceFree,
    selectedItemId,
    typeName,
    groupName, // Destructure groupName prop
    context,
    cartLines,
    cartLineId,
    onChange
}) => {
    const count = deliveryOptions.length;
    if (count === 0) {
        return null;
    }

    const changeCurrentDeliveryOption = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            onChange(event, cartLines, cartLineId);
        },
        [onChange, cartLines, cartLineId]
    );
    // const selectedDeliveryOptionItem = deliveryOptions.length > 0 ? deliveryOptions[0].Code || '' : ''; // By default, select first delivery
    const deliveryClassName = `${className}__radio-btn-grp`;
    const optionClassName = `${deliveryClassName}-opt`;
    const response = deliveryOptions.map((deliveryOption: DeliveryOption, index: number) => (
        <div key={index} className={optionClassName}>
            <RadioButtonComponent
                {...{
                    groupName: groupName,
                    isChecked: deliveryOption.Code === selectedItemId,
                    value: deliveryOption.Code,
                    ariaSetSize: count,
                    ariaPosInSet: index + 1,
                    ariaLabel: `${deliveryOption.Description!}`,
                    onChange: changeCurrentDeliveryOption,
                    telemetryContent: telemetryContent,
                    className: `${optionClassName}-btn`
                }}
            />
            <DescriptionComponent
                {...{
                    description: deliveryOption.Description,
                    className: `${optionClassName}-dsc`
                }}
            />
        </div>
    ));

    return <div className={deliveryClassName}>{response}</div>;
};

export default CheckoutCartLineDeliveryOptionsRadio;
